import React, { useState, useMemo, useEffect } from 'react';
import { Row, Col, Card, Spinner, Form, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSync,
  faFilter,
  faArrowUp,
  faArrowDown,
  faMinus
} from '@fortawesome/free-solid-svg-icons';
import Select from 'react-select';
import moment from 'moment';
import { useQuery } from 'react-query';
// import { ChartFactory } from '../../../AIDashboard/AIDashboard/utils';
import { ChartFactory } from '../../../ChartUtility/charts';
import { useGraphData } from '../hooks/useMeters';
import { getKPIs, buildWidgetConfig } from '../services/meterApi';
import '../styles/MetersDashboard.css';

const DefaultMeterDashboard = ({ 
  financialYearOptions,
  sourceOptions,
  onRefresh 
}) => {
  const [lastRefreshTime, setLastRefreshTime] = useState(moment());

  // Global Filters
  const [selectedFinancialYear, setSelectedFinancialYear] = useState();
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedSubLocation, setSelectedSubLocation] = useState(null);


  useEffect(() => {
    if (financialYearOptions) {
      setSelectedFinancialYear(financialYearOptions?.[0]?.value);
    }
  }, [financialYearOptions]);

  // Fetch KPIs
  const kpiType = 'EXTERNAL'; // or from state/props

  const { data: kpisData, isLoading: kpisLoading, error: kpisError, refetch: refetchKPIs } = useQuery(
    ['kpis', { kpiType }],
    ({ queryKey }) => {
      const [, params] = queryKey;
      return getKPIs(params);
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000 // 5 minutes
    }
  );

  // Extract unique categories
  const categories = useMemo(() => {
    if (!kpisData || !Array.isArray(kpisData)) return [];
    
    const uniqueCategories = [...new Set(kpisData.map(kpi => kpi.category))];
    return uniqueCategories.filter(Boolean);
  }, [kpisData]);

  // Get sub-locations based on selected location
  const subLocationOptions = useMemo(() => {
    if (!selectedLocation) return [];
    const location = sourceOptions.find(s => s.value === selectedLocation.value);
    return location?.subLocations || [];
  }, [selectedLocation, sourceOptions]);

  // Filter KPIs based on global filters
  const filteredKPIs = useMemo(() => {
    if (!kpisData) return [];
    
    let filtered = [...kpisData];
    
    // Apply filters here if needed based on your KPI structure
    // For now, returning all KPIs
    
    return filtered;
  }, [kpisData, selectedFinancialYear, selectedLocation, selectedSubLocation]);

  // Group KPIs by category
  const kpisByCategory = useMemo(() => {
    const grouped = {};
    
    filteredKPIs.forEach(kpi => {
      if (!grouped[kpi.category]) {
        grouped[kpi.category] = {
          category: kpi.category,
          subCategories: {}
        };
      }
      
      if (!grouped[kpi.category].subCategories[kpi.subCategory]) {
        grouped[kpi.category].subCategories[kpi.subCategory] = [];
      }
      
      grouped[kpi.category].subCategories[kpi.subCategory].push(kpi);
    });
    
    return grouped;
  }, [filteredKPIs]);

  const handleManualRefresh = () => {
    onRefresh?.();
    refetchKPIs();
    setLastRefreshTime(moment());
  };

  if (kpisLoading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading dashboard...</p>
      </div>
    );
  }

  if (kpisError) {
    return (
      <div className="text-center p-5">
        <Card>
          <Card.Body>
            <p className="text-danger">Failed to load KPIs data</p>
            <Button variant="primary" onClick={handleManualRefresh}>
              <FontAwesomeIcon icon={faSync} className="me-2" />
              Retry
            </Button>
          </Card.Body>
        </Card>
      </div>
    );
  }

  return (
    <div className="meters-dashboard">
      {/* Header with Refresh */}
      <div className="dashboard-header mb-4">
        <h2>KPI Dashboard</h2>
        <div className="header-controls">
          <Button 
            variant="link" 
            className="refresh-icon-btn"
            onClick={handleManualRefresh}
            title="Refresh dashboard"
          >
            <FontAwesomeIcon icon={faSync} />
          </Button>
          <span className="last-refresh">
            Last updated: {lastRefreshTime.format('HH:mm:ss')}
          </span>
        </div>
      </div>

      {/* Global Filters */}
      <Card className="mb-4">
        <Card.Header>
          <h5>
            <FontAwesomeIcon icon={faFilter} className="me-2" />
            Global Filters
          </h5>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Financial Year</Form.Label>
                <Select
                  options={financialYearOptions}
                  value={financialYearOptions.find(opt => opt.value === selectedFinancialYear)}
                  onChange={(selected) => setSelectedFinancialYear(selected?.value)}
                  placeholder="Select Financial Year"
                  className="react-select-container"
                  classNamePrefix="react-select"
                  closeMenuOnSelect={false}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  isClearable
                />
              </Form.Group>
            </Col>
            
            <Col md={4}>
              <Form.Group>
                <Form.Label>Location</Form.Label>
                <Select
                  options={sourceOptions}
                  value={selectedLocation}
                  onChange={(selected) => {
                    setSelectedLocation(selected);
                    setSelectedSubLocation(null);
                  }}
                  placeholder="Select Location"
                  className="react-select-container"
                  classNamePrefix="react-select"
                  closeMenuOnSelect={false}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  isClearable
                />
              </Form.Group>
            </Col>
            
            <Col md={4}>
              <Form.Group>
                <Form.Label>Sub Location</Form.Label>
                <Select
                  options={subLocationOptions}
                  value={selectedSubLocation}
                  onChange={setSelectedSubLocation}
                  placeholder="Select Sub Location"
                  className="react-select-container"
                  classNamePrefix="react-select"
                  closeMenuOnSelect={false}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  isClearable
                  isDisabled={!selectedLocation}
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Summary Section */}
      <div className="summary-section mb-4">
        <h3 className="section-title">Today's Summary</h3>
        <Row>
          {selectedFinancialYear && categories.map((category, index) => (
            <Col md={4} key={index} className="mb-3">
              <CategorySummaryCard
                category={category}
                kpisData={filteredKPIs.filter(kpi => kpi.category === category)}
                currentFY={selectedFinancialYear}
                locationId={selectedLocation?.value}
                subLocationId={selectedSubLocation?.value}
              />
            </Col>
          ))}
        </Row>
      </div>

      {/* Graphs Section */}
      <div className="graphs-section">
        <h3 className="section-title">Category-wise Analysis</h3>
        {categories.map((category, index) => (
          <CategoryGraphSection
            key={index}
            kpisData={filteredKPIs.filter(kpi => kpi.category === category)}
            category={category}
            kpisByCategory={kpisByCategory[category]}
            currentFY={selectedFinancialYear}
            locationId={selectedLocation?.value}
            subLocationId={selectedSubLocation?.value}
          />
        ))}
      </div>

      {categories.length === 0 && (
        <Card>
          <Card.Body className="text-center p-5">
            <p className="text-muted">No KPIs data available.</p>
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

// Category Summary Card - Shows today's and yesterday's consumption
const CategorySummaryCard = ({ category, kpisData, currentFY, locationId, subLocationId }) => {
  // Get all meter IDs for this category
  const meterIds = useMemo(() => {
    // This would need to be fetched from your meters data
    // For now, returning empty array
    return [];
  }, [kpisData]);

  // Build config to fetch today and yesterday data in single call
  const summaryConfig = useMemo(() => {
    if (!kpisData || kpisData.length === 0) return null;

    const firstKpi = kpisData[0];
    
    return buildWidgetConfig({
      meterId: meterIds,
      financialYear: currentFY,
      startDateTime: moment().subtract(2, 'days').startOf('day').toISOString(), // Yesterday 00:00:00
      endDateTime: moment().toISOString(), // Now
      locationId: locationId?.toString() || '1',
      subLocationId: subLocationId?.toString(),
      module: firstKpi.module,
      category: category,
      kpiType: 'EXTERNAL',
      aggregationPeriodValue: 1,
      aggregationPeriodUnit: 'days',
      xAxisField: 'displayPeriods',
      stackByField: 'category'
    });
  }, [category, kpisData, meterIds, currentFY, locationId, subLocationId]);

  const { data, isLoading, error } = useGraphData(
    summaryConfig, 
    { 
      enabled: !!summaryConfig && !!currentFY
    }
  );

  const stats = useMemo(() => {
    if (!data?.data) return null;

    const dataArray = Array.isArray(data.data) ? data.data : data.data.data;
    if (!dataArray) return null;

    const today = moment().startOf('day');
    const yesterday = moment().subtract(1, 'day').startOf('day');

    // Find today's and yesterday's data by matching displayPeriods
    const todayData = dataArray.find(item => 
      item.period_ts && moment(item.period_ts).isSame(today, 'day')
    );
    
    const yesterdayData = dataArray.find(item => 
      item.period_ts && moment(item.period_ts).isSame(yesterday, 'day')
    );
    
    const todayValue = todayData ? Number(todayData.sum_value || 0) : 0;
    const yesterdayValue = yesterdayData ? Number(yesterdayData.sum_value || 0) : 0;
    const change = yesterdayValue ? ((todayValue - yesterdayValue) / yesterdayValue) * 100 : 0;
    const changeValue = todayValue - yesterdayValue;

    return { 
      todayValue, 
      yesterdayValue, 
      change, 
      changeValue 
    };
  }, [data]);

  if (isLoading) {
    return (
      <Card className="kpi-card">
        <Card.Body>
          <h6 className="kpi-card-title">{category}</h6>
          <div className="text-center">
            <Spinner animation="border" size="sm" variant="primary" />
          </div>
        </Card.Body>
      </Card>
    );
  }

  if (!stats || error) {
    return (
      <Card className="kpi-card">
        <Card.Body>
          <h6 className="kpi-card-title">{category}</h6>
          <div className="text-center">
            <small className="text-muted">No data available</small>
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="kpi-card">
      <Card.Body>
        <h6 className="kpi-card-title">{category}</h6>
        <p className="kpi-card-subtitle">Today's Consumption</p>
        <div className="kpi-value">
          {stats.todayValue.toLocaleString(undefined, { maximumFractionDigits: 2 })}
          <span className="kpi-unit">m³</span>
        </div>
        <div className={`kpi-change ${stats.change > 0 ? 'positive' : stats.change < 0 ? 'negative' : 'neutral'}`}>
          <FontAwesomeIcon icon={stats.change > 0 ? faArrowUp : stats.change < 0 ? faArrowDown : faMinus} className="me-1" />
          <span>{Math.abs(stats.change).toFixed(1)}%</span>
          <span className="change-value ms-2">
            ({stats.change > 0 ? '+' : ''}{stats.changeValue.toFixed(2)} m³)
          </span>
        </div>
        <small className="text-muted">vs Yesterday</small>
        <div className="mt-2">
          <small className="text-muted">Yesterday: {stats.yesterdayValue.toFixed(2)} m³</small>
        </div>
      </Card.Body>
    </Card>
  );
};

// Category Graph Section - Shows 3 graphs per category
const CategoryGraphSection = ({ category, kpisData, kpisByCategory, currentFY, locationId, subLocationId }) => {
  if (!kpisByCategory) return null;

  const subCategories = Object.keys(kpisByCategory.subCategories);
  const validSubCategories = Object.keys(kpisByCategory.subCategories || {})
  .filter(sc => sc && sc.trim() !== '');
  const firstKpi = kpisData?.[0];

  return (
    <div className="category-section mb-5">
      <h4 className="mb-4">{category}</h4>
      
      {/* 1. Category Level Graph */}
      <KpiGraph
        title={`${category} - Category Level`}
        currentFY={currentFY}
        locationId={locationId}
        subLocationId={subLocationId}
        module={firstKpi.module}
        category={category}
        stackByField="category"
        filterConfig={{ category: [category] }}
      />

      {/* 2️⃣ Sub-Category Level Graph (ONLY if valid subCategories exist) */}
      {validSubCategories.length > 0 && (
        <KpiGraph
          title={`${category} - Sub-Category Level`}
          currentFY={currentFY}
          locationId={locationId}
          subLocationId={subLocationId}
          module={firstKpi.module}
          category={category}
          stackByField="sub_category"
          filterConfig={{ category: [category] }}
        />
      )}

      {/* 3. KPI Level Graphs for each sub-category */}
      {subCategories.map((subCategory, index) => (
        <KpiGraph
          key={index}
          title={`${category} - ${subCategory ? `${subCategory} - ` : ''} KPI Level`}
          currentFY={currentFY}
          locationId={locationId}
          subLocationId={subLocationId}
          module={firstKpi.module}
          category={category}
          subCategory={subCategory}
          stackByField="kpi"
          filterConfig={{
            category: [category],
            sub_category: [subCategory]
          }}
        />
      ))}
    </div>
  );
};

// KPI Graph Component with local filters
const KpiGraph = ({ 
  title, 
  currentFY, 
  locationId, 
  subLocationId, 
  module,
  category, 
  subCategory, 
  stackByField,
  filterConfig 
}) => {
  // Local filters (similar to MeterDetails)
  const [timeRange, setTimeRange] = useState('week');
  const [aggregation, setAggregation] = useState('day');
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);
  const [debouncedConfig, setDebouncedConfig] = useState(null);

  const lastChanged = React.useRef(null);
  const isInitialMount = React.useRef(true);
  const debounceTimer = React.useRef(null); 

  // Sync time range and aggregation
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (lastChanged.current === 'timeRange') {
      const defaultAggregations = {
        'hour': 'minute',
        'day': 'hour',
        'week': 'day',
        'month': 'day',
        'custom': 'hour'
      };
      setAggregation(defaultAggregations[timeRange]);
    }
  }, [timeRange]);

  useEffect(() => {
    if (isInitialMount.current) return;

    if (lastChanged.current === 'aggregation') {
      const defaultTimeRanges = {
        'minute': 'hour',
        'hour': 'day',
        'day': 'week',
        'month': 'month'
      };
      const newTimeRange = defaultTimeRanges[aggregation];
      if (newTimeRange && timeRange !== 'custom') {
        setTimeRange(newTimeRange);
      }
    }
  }, [aggregation, timeRange]);

  const handleTimeRangeChange = (e) => {
    lastChanged.current = 'timeRange';
    setTimeRange(e.target.value);
  };

  const handleAggregationChange = (e) => {
    lastChanged.current = 'aggregation';
    setAggregation(e.target.value);
  };

  // Calculate date range
  const dateRange = useMemo(() => {
    const now = moment();
    switch (timeRange) {
      case 'hour':
        return { start: moment().subtract(1, 'hour'), end: now };
      case 'day':
        return { start: moment().subtract(1, 'day'), end: now };
      case 'week':
        return { start: moment().subtract(7, 'days'), end: now };
      case 'month':
        return { start: moment().subtract(1, 'month'), end: now };
      case 'custom':
        return {
          start: customStart || moment().subtract(7, 'days'),
          end: customEnd || now
        };
      default:
        return { start: moment().subtract(7, 'days'), end: now };
    }
  }, [timeRange, customStart, customEnd]);

  // Build graph config
  const graphConfig = useMemo(() => {
    const config = {
      financialYear: currentFY,
      startDateTime: dateRange.start.toISOString(),
      endDateTime: dateRange.end.toISOString(),
      locationId: locationId?.toString() || '1',
      module,
      category,
      kpiType: 'EXTERNAL',
      aggregationPeriodValue: 1,
      aggregationPeriodUnit: aggregation === 'minute' ? 'minutes' : aggregation === 'hour' ? 'hours' : aggregation === 'day' ? 'days' : 'months',
      xAxisField: 'displayPeriods',
      stackByField: stackByField,
      chartType: 'line',
      enableStacking: true
    };

    if (subLocationId) config.subLocationId = subLocationId?.toString();
    if (subCategory) config.subCategory = subCategory;

    return buildWidgetConfig(config);
  }, [currentFY, dateRange, locationId, subLocationId, category, subCategory, aggregation, stackByField]);


  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      setDebouncedConfig(graphConfig);
    }, 300);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [graphConfig]);

  const { data: graphData, isLoading } = useGraphData(
    debouncedConfig, 
    { 
      enabled: !!debouncedConfig && !!currentFY
    }
  );

  return (
    <Card className="graph-card mb-4">
      <Card.Header>
        <h5 className="mb-0">{title}</h5>
      </Card.Header>
      <Card.Body>
        {/* Local Filters */}
        <div className="graph-controls mb-3">
          <Row className="align-items-end">
            <Col md={3}>
              <Form.Group>
                <Form.Label className="small">Time Range</Form.Label>
                <Form.Select size="sm" value={timeRange} onChange={handleTimeRangeChange}>
                  <option value="hour">Last Hour</option>
                  <option value="day">Last 24 Hours</option>
                  <option value="week">Last 7 Days</option>
                  <option value="month">Last 30 Days</option>
                  <option value="custom">Custom Range</option>
                </Form.Select>
              </Form.Group>
            </Col>

            {timeRange === 'custom' && (
              <>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="small">Start Date</Form.Label>
                    <Form.Control
                      type="datetime-local"
                      size="sm"
                      value={customStart ? customStart.format('YYYY-MM-DDTHH:mm') : ''}
                      onChange={(e) => setCustomStart(moment(e.target.value))}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="small">End Date</Form.Label>
                    <Form.Control
                      type="datetime-local"
                      size="sm"
                      value={customEnd ? customEnd.format('YYYY-MM-DDTHH:mm') : ''}
                      onChange={(e) => setCustomEnd(moment(e.target.value))}
                    />
                  </Form.Group>
                </Col>
              </>
            )}

            <Col md={3}>
              <Form.Group>
                <Form.Label className="small">Aggregation</Form.Label>
                <Form.Select size="sm" value={aggregation} onChange={handleAggregationChange}>
                  <option value="minute">Per Minute</option>
                  <option value="hour">Hourly</option>
                  <option value="day">Daily</option>
                  <option value="month">Monthly</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </div>

        {/* Graph */}
        <div className="graph-container">
          {isLoading ? (
            <div className="text-center p-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <ChartFactory
              chartData={{
                data: graphData?.data || [],
                chartType: 'line',
                title: title
              }}
              chartOptions={{
                height: 350,
                stacked: true,
                transform: true
              }}
              group_by={['displayPeriods', stackByField]}
            />
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

export default DefaultMeterDashboard;